import React, { FC } from 'react';
import { useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import { toast, TypeOptions } from 'react-toastify';

import { InfoFilledIcon } from 'src/shared/components/icons';
import { useTranslate } from 'src/shared/providers';
import './ConfirmationToast.styles.scss';

interface Props {
  title: string;
  messages: string[];
  type: TypeOptions;
  currentKey?: string;
  submitText?: string;
  cancelText?: string;
  onSubmit?: (key?: string) => void;
  onCancel?: (key?: string) => void;
  showCancelButton?: boolean;
}

const ConfirmationToast: FC<Props> = ({
  title,
  messages,
  currentKey,
  cancelText,
  submitText,
  onSubmit,
  onCancel,
  showCancelButton = true,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();

  return (
    <div className={`d-flex ${theme.palette.mode}`}>
      <div className="me-2">
        <InfoFilledIcon />
      </div>
      <div className="mt-1">
        <p className="h6 fw-500">{title}</p>
        {messages.map((message, index) => (
          <p className="text-muted fw-400 mb-2" key={`error-message-toast-${index}`}>
            {message}
          </p>
        ))}
        {onCancel && showCancelButton && (
          <Button
            variant="text"
            color="inherit"
            size="large"
            className="confirmation-toast-btn fw-500 p-0 me-3"
            onClick={() => onCancel(currentKey)}
          >
            {cancelText || t('global.cancel')}
          </Button>
        )}
        {onSubmit && (
          <Button
            variant="text"
            color="info"
            size="large"
            className="confirmation-toast-btn fw-500 p-0 "
            onClick={() => onSubmit(currentKey)}
          >
            {submitText || t('global.okay')}
          </Button>
        )}
      </div>
    </div>
  );
};

export const showConfirmationToast = ({ onSubmit, onCancel, type, ...resProps }: Props) => {
  toast.info(
    ({ closeToast }) => {
      const handleSubmit = (key?: string) => {
        if (onSubmit) {
          onSubmit(key);
        }

        if (closeToast) {
          closeToast();
        }
      };

      const handleCancel = (key?: string) => {
        if (!onCancel) {
          return;
        }

        onCancel(key);

        if (closeToast) {
          closeToast();
        }
      };

      return (
        <ConfirmationToast
          {...resProps}
          type={type}
          onSubmit={handleSubmit}
          onCancel={onCancel ? handleCancel : undefined}
        />
      );
    },
    {
      type,
      position: 'top-right',
      autoClose: false,
      pauseOnHover: true,
      icon: false,
      onClose: () => {
        if (!onCancel) {
          return;
        }

        onCancel(resProps.currentKey);
      },
    },
  );
};
